import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  FiArrowLeft, 
  FiDownload, 
  FiClock,
  FiCalendar,
  FiUser,
  FiFileText
} from 'react-icons/fi';
import axios from 'axios';
import moment from 'moment';
import { BarLoader } from 'react-spinners';

const ViewRequests = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [csoNames, setCsoName] = useState('');
  // Fetch request details
 useEffect(() => {
  const fetchRequest = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/request/ById/${id}`, 
        { withCredentials: true }
      );
      setRequest(response.data);

      // Now that we have the response, fetch CSO name
      const csoNameResponse = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/cso/registration/${response.data.registration_id}`
      );
      setCsoName(csoNameResponse.data);
      
      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to fetch request details');
      console.error('Error fetching request:', err);
    } finally {
      setLoading(false);
    }
  };

  fetchRequest();
}, [id]);

const getFolderName = (name = "default") =>
  name.replace(/\s+/g, "_").toLowerCase();

const formattedCsoName = csoNames.csoName ? getFolderName(csoNames.csoName) : "";

const handleDownload = () => {
  if (request?.request_file) {
    window.open(
      `${process.env.REACT_APP_API_URL}/${formattedCsoName}/${request.request_file}`,
      "_blank"
    );
  }
};

// const formattedCsoName = csoNames.csoName ? csoNames.csoName.replace(/\s+/g, "_"): "";

//   // Handle download
//   const handleDownload = () => {
//     if (request?.request_file) {
//       window.open(`${process.env.REACT_APP_API_URL}/${formattedCsoName}/${request.request_file}`, '_blank');
//     }
//   };

  // Handle print


  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <BarLoader color="#4F46E5" size={50} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-red-50 border-l-4 border-red-500 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">
                {error}. <button onClick={() => window.location.reload()} className="text-red-700 font-medium underline">Try again</button>
              </p>
            </div>
          </div>
        </div>
        <button
          onClick={() => navigate(-1)}
          className="mt-4 flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
        >
          <FiArrowLeft className="mr-2" /> Back to Requests
        </button>
      </div>
    );
  }

  if (!request) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-yellow-700">
                No request found with the specified ID.
              </p>
            </div>
          </div>
        </div>
        <button
          onClick={() => navigate(-1)}
          className="mt-4 flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
        >
          <FiArrowLeft className="mr-2" /> Back to Requests
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Header with actions */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
        <div>
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-blue-600 hover:text-blue-800 mb-4 md:mb-0"
          >
            <FiArrowLeft className="mr-2" /> Back to Requests
          </button>
          <h1 className="text-2xl font-bold text-gray-800">Request Details</h1>
          <p className="text-sm text-gray-500 mt-1">ID: {request.registration_id}</p>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={handleDownload}
            disabled={!request.request_file}
            className={`flex items-center px-3 py-2 rounded-md text-sm font-medium ${request.request_file ? 'bg-blue-100 text-blue-700 hover:bg-blue-200' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`}
          >
            <FiDownload className="mr-2" /> Download
          </button>
      
        </div>
      </div>

      {/* Main content */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        {/* Status bar */}
        <div className={`px-6 py-4 ${request.status === 'approve' ? 'bg-green-50 border-l-4 border-green-500' :
          request.status === 'pending' ? 'bg-yellow-50 border-l-4 border-yellow-500' :
          request.status === 'reject' ? 'bg-red-50 border-l-4 border-red-500' :
          request.status === 'inprogress' ? 'bg-blue-50 border-l-4 border-blue-500' :
          'bg-gray-50 border-l-4 border-gray-500'}`}
        >
          <div className="flex items-center">
            <span className={`font-medium ${request.status === 'approve' ? 'text-green-800' :
              request.status === 'pending' ? 'text-yellow-800' :
              request.status === 'reject' ? 'text-red-800' :
              request.status === 'inprogress' ? 'text-blue-800' :
              'text-gray-800'}`}
            >
              Status: {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
            </span>
          </div>
        </div>

        {/* Request details */}
        <div className="px-6 py-4 grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Left column */}
          <div>
            <div className="mb-6">
              <h2 className="text-lg font-medium text-gray-900 mb-2 flex items-center">
                <FiFileText className="mr-2 text-blue-500" /> Request Information
              </h2>
              <div className="space-y-4">
                <div>
                  <p className="text-sm font-medium text-gray-500">Category</p>
                  <p className="mt-1 text-sm text-gray-900">{request.category_name}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Request Type</p>
                  <p className="mt-1 text-sm text-gray-900">{request.request_name}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Description</p>
                  <p className="mt-1 text-sm text-gray-900 whitespace-pre-line">{request.description}</p>
                </div>
              </div>
            </div>

            <div>
              <h2 className="text-lg font-medium text-gray-900 mb-2 flex items-center">
                <FiUser className="mr-2 text-blue-500" /> User Information
              </h2>
              <div className="space-y-4">
                <div>
                  <p className="text-sm font-medium text-gray-500">Submitted By</p>
                  <p className="mt-1 text-sm text-gray-900">
                    {request.user_id ? `User name: ${csoNames.csoName}` : 'Not specified'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right column */}
          <div>
            <div className="mb-6">
              <h2 className="text-lg font-medium text-gray-900 mb-2 flex items-center">
                <FiCalendar className="mr-2 text-blue-500" /> Timeline
              </h2>
              <div className="space-y-4">
                <div>
                  <p className="text-sm font-medium text-gray-500">Date Submitted</p>
                  <p className="mt-1 text-sm text-gray-900">
                    {moment(request.created_at).format('MMMM D, YYYY [at] h:mm A')}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Last Updated</p>
                  <p className="mt-1 text-sm text-gray-900">
                    {moment(request.updated_at).format('MMMM D, YYYY [at] h:mm A')}
                  </p>
                </div>
              </div>
            </div>

            <div>
              <h2 className="text-lg font-medium text-gray-900 mb-2 flex items-center">
                <FiClock className="mr-2 text-blue-500" /> Processing Information
              </h2>
              <div className="space-y-4">
                <div>
                  <p className="text-sm font-medium text-gray-500">Duration</p>
                  <p className="mt-1 text-sm text-gray-900">
                    {moment(request.created_at).fromNow(true)} old
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Attached File</p>
                  {request.request_file ? (
                    <button
                      onClick={handleDownload}
                      className="mt-1 flex items-center text-blue-600 hover:text-blue-800"
                    >
                      <FiDownload className="mr-1" /> {request.request_file}
                    </button>
                  ) : (
                    <p className="mt-1 text-sm text-gray-500">No file attached</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

    
      </div>

      {/* Print-specific styles (hidden on screen) */}

    </div>
  );
};

export default ViewRequests;